import React, { FC, useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    List,
    ListItem,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { DragDropContext, Draggable, DropResult, Droppable } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';
import { ListViewColumn, ListViewProfile, ListViewProfileInput, ListViewType } from 'models/organization';
import {
    isKnownOrderListColumnKey,
    isSystemDefaultProfile,
    ORDER_LIST_COLUMN_DEFINITIONS,
    OrderListColumnKey,
} from 'components/orders/lab-order-table/column-registry';

type ListViewProfileDialogProps = {
    open: boolean;
    listType: ListViewType;
    profile?: ListViewProfile;
    saving: boolean;
    panNumberEnabled?: boolean;
    onClose: () => void;
    onSave: (input: ListViewProfileInput) => Promise<unknown>;
};

const ListViewProfileDialog: FC<ListViewProfileDialogProps> = ({
    open,
    listType,
    profile,
    saving,
    panNumberEnabled = false,
    onClose,
    onSave,
}) => {
    const { t: translate } = useTranslation(['lab-settings', 'common', 'orders', 'workflow', 'transport']);
    const [name, setName] = useState('');
    const [selectedKeys, setSelectedKeys] = useState<OrderListColumnKey[]>([]);
    const [showIconArea, setShowIconArea] = useState(true);
    const [defaultSortField, setDefaultSortField] = useState<string>('');
    const [defaultSortDirection, setDefaultSortDirection] = useState<'asc' | 'desc'>('desc');
    const [addColumnInput, setAddColumnInput] = useState('');

    const seedKeys = useMemo<OrderListColumnKey[]>(() => {
        if (!profile) return [];
        return profile.columns
            .filter((column) => column.visible)
            .map((column) => column.key)
            .filter(isKnownOrderListColumnKey);
    }, [profile]);

    useEffect(() => {
        if (!open) return;
        setName(profile?.name ?? '');
        setSelectedKeys(seedKeys);
        setShowIconArea(profile?.showIconArea ?? true);
        setDefaultSortField(profile?.defaultSortField ?? '');
        setDefaultSortDirection(profile?.defaultSortDirection ?? 'desc');
    }, [open, profile, seedKeys]);

    const getColumnLabel = (key: OrderListColumnKey) => {
        const definition = ORDER_LIST_COLUMN_DEFINITIONS[key];
        return translate(`${definition.i18nNamespace}:${definition.labelKey}`);
    };

    const availableKeys = useMemo<OrderListColumnKey[]>(() => {
        const selected = new Set(selectedKeys);
        return (Object.keys(ORDER_LIST_COLUMN_DEFINITIONS) as OrderListColumnKey[])
            .filter((key) => !selected.has(key))
            .filter((key) => key !== 'panNumber' || panNumberEnabled)
            .sort((a, b) => getColumnLabel(a).localeCompare(getColumnLabel(b)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedKeys, translate, panNumberEnabled]);

    const sortableOptions = useMemo(
        () =>
            selectedKeys
                .map((key) => ({ key, sortable: ORDER_LIST_COLUMN_DEFINITIONS[key].sortable }))
                .filter((entry): entry is { key: OrderListColumnKey; sortable: { field: string } } =>
                    Boolean(entry.sortable),
                ),
        [selectedKeys],
    );

    const isSortFieldValid = useMemo(
        () => sortableOptions.some((option) => option.sortable.field === defaultSortField),
        [sortableOptions, defaultSortField],
    );

    useEffect(() => {
        if (defaultSortField && !isSortFieldValid) {
            setDefaultSortField('');
        }
    }, [defaultSortField, isSortFieldValid]);

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination || result.source.index === result.destination.index) return;
        const next = [...selectedKeys];
        const [moved] = next.splice(result.source.index, 1);
        next.splice(result.destination.index, 0, moved);
        setSelectedKeys(next);
    };

    const handleAddColumn = (key: OrderListColumnKey | null) => {
        if (!key) return;
        setAddColumnInput('');
        if (selectedKeys.includes(key)) return;
        setSelectedKeys((prev) => [...prev, key]);
    };

    const handleRemoveColumn = (key: OrderListColumnKey) => {
        setSelectedKeys((prev) => prev.filter((k) => k !== key));
    };

    const handleSave = async () => {
        const trimmedName = name.trim();
        if (!trimmedName) return;
        const columns: ListViewColumn[] = selectedKeys.map((key) => ({ key, visible: true }));
        const sortField = isSortFieldValid && defaultSortField ? defaultSortField : undefined;
        await onSave({
            profileId: profile && !isSystemDefaultProfile(profile) ? profile._id : undefined,
            listType,
            name: trimmedName,
            columns,
            showIconArea,
            defaultSortField: sortField,
            defaultSortDirection: sortField ? defaultSortDirection : undefined,
        });
    };

    const getColumnDescription = (key: OrderListColumnKey) => {
        const definition = ORDER_LIST_COLUMN_DEFINITIONS[key];
        return translate(definition.descriptionKey);
    };

    const isSaveDisabled = saving || !name.trim() || selectedKeys.length === 0;

    const title = profile
        ? translate('lab-settings:LIST_VIEW_PROFILE_EDIT_TITLE')
        : translate('lab-settings:LIST_VIEW_PROFILE_ADD_TITLE');

    return (
        <DHDialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            title={title}
            actions={
                <Button onClick={handleSave} variant="contained" disabled={isSaveDisabled}>
                    {translate('common:SAVE')}
                </Button>
            }
        >
            <Stack spacing={2} mt={1}>
                <TextField
                    label={translate('common:NAME')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    autoFocus
                />
                <Box>
                    <Typography variant="subtitle2" mb={1}>
                        {translate('lab-settings:LIST_VIEW_PROFILE_COLUMNS')}
                    </Typography>
                    <Paper variant="outlined">
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="list-view-columns">
                                {(provided) => (
                                    <List dense ref={provided.innerRef} {...provided.droppableProps}>
                                        {selectedKeys.map((key, index) => {
                                            const isHiddenByDisabledFeature = key === 'panNumber' && !panNumberEnabled;
                                            return (
                                                <Draggable key={key} draggableId={key} index={index}>
                                                    {(dragProvided, dragSnapshot) => (
                                                        <ListItem
                                                            ref={dragProvided.innerRef}
                                                            {...dragProvided.draggableProps}
                                                            secondaryAction={
                                                                <IconButton
                                                                    edge="end"
                                                                    onClick={() => handleRemoveColumn(key)}
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            }
                                                            sx={{
                                                                background: dragSnapshot.isDragging
                                                                    ? 'action.hover'
                                                                    : undefined,
                                                            }}
                                                        >
                                                            <Box
                                                                {...dragProvided.dragHandleProps}
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    mr: 2,
                                                                    cursor: 'grab',
                                                                    color: dragSnapshot.isDragging
                                                                        ? 'primary.main'
                                                                        : 'action.active',
                                                                    opacity: isHiddenByDisabledFeature ? 0.6 : 1,
                                                                }}
                                                            >
                                                                <DragIndicatorIcon />
                                                            </Box>
                                                            <Box
                                                                flexGrow={1}
                                                                sx={{ opacity: isHiddenByDisabledFeature ? 0.6 : 1 }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1,
                                                                    }}
                                                                >
                                                                    <Typography variant="body2">
                                                                        {getColumnLabel(key)}
                                                                    </Typography>
                                                                    {isHiddenByDisabledFeature ? (
                                                                        <Tooltip
                                                                            title={translate(
                                                                                'lab-settings:LIST_VIEW_PROFILE_PAN_DISABLED_HINT',
                                                                            )}
                                                                        >
                                                                            <Chip
                                                                                size="small"
                                                                                variant="outlined"
                                                                                label={translate(
                                                                                    'lab-settings:LIST_VIEW_PROFILE_COLUMN_HIDDEN',
                                                                                )}
                                                                            />
                                                                        </Tooltip>
                                                                    ) : undefined}
                                                                </Box>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {isHiddenByDisabledFeature
                                                                        ? translate(
                                                                              'lab-settings:LIST_VIEW_PROFILE_PAN_DISABLED_HINT',
                                                                          )
                                                                        : getColumnDescription(key)}
                                                                </Typography>
                                                            </Box>
                                                        </ListItem>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                        <ListItem>
                                            <Autocomplete<OrderListColumnKey>
                                                fullWidth
                                                size="small"
                                                options={availableKeys}
                                                value={null}
                                                inputValue={addColumnInput}
                                                onInputChange={(_, value, reason) => {
                                                    if (reason === 'reset') return;
                                                    setAddColumnInput(value);
                                                }}
                                                onChange={(_, key) => handleAddColumn(key)}
                                                getOptionLabel={(option) => getColumnLabel(option)}
                                                noOptionsText={translate(
                                                    'lab-settings:LIST_VIEW_PROFILE_NO_AVAILABLE_COLUMNS',
                                                )}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={translate('lab-settings:LIST_VIEW_PROFILE_ADD_COLUMN')}
                                                    />
                                                )}
                                                renderOption={(props, option) => {
                                                    const isSortable = Boolean(
                                                        ORDER_LIST_COLUMN_DEFINITIONS[option].sortable,
                                                    );
                                                    return (
                                                        <li {...props} key={option}>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1,
                                                                    width: '100%',
                                                                }}
                                                            >
                                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                    <Typography variant="body2">
                                                                        {getColumnLabel(option)}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                    >
                                                                        {getColumnDescription(option)}
                                                                    </Typography>
                                                                </Box>
                                                                {isSortable ? (
                                                                    <SwapVertIcon fontSize="small" color="action" />
                                                                ) : null}
                                                            </Box>
                                                        </li>
                                                    );
                                                }}
                                            />
                                        </ListItem>
                                    </List>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </Paper>
                </Box>
                <Box>
                    <Typography variant="subtitle2" mb={1}>
                        {translate('lab-settings:LIST_VIEW_PROFILE_DEFAULT_SORT')}
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="default-sort-field-label">
                                {translate('lab-settings:LIST_VIEW_PROFILE_DEFAULT_SORT_FIELD')}
                            </InputLabel>
                            <Select
                                labelId="default-sort-field-label"
                                value={defaultSortField}
                                label={translate('lab-settings:LIST_VIEW_PROFILE_DEFAULT_SORT_FIELD')}
                                onChange={(e) => setDefaultSortField(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>{translate('lab-settings:LIST_VIEW_PROFILE_DEFAULT_SORT_NONE')}</em>
                                </MenuItem>
                                {sortableOptions.map(({ key, sortable }) => (
                                    <MenuItem key={key} value={sortable.field}>
                                        {getColumnLabel(key)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 160 }} disabled={!defaultSortField}>
                            <InputLabel id="default-sort-direction-label">
                                {translate('lab-settings:LIST_VIEW_PROFILE_DEFAULT_SORT_DIRECTION')}
                            </InputLabel>
                            <Select
                                labelId="default-sort-direction-label"
                                value={defaultSortDirection}
                                label={translate('lab-settings:LIST_VIEW_PROFILE_DEFAULT_SORT_DIRECTION')}
                                onChange={(e) => setDefaultSortDirection(e.target.value as 'asc' | 'desc')}
                            >
                                <MenuItem value="desc">
                                    {translate('lab-settings:LIST_VIEW_PROFILE_SORT_DESC')}
                                </MenuItem>
                                <MenuItem value="asc">{translate('lab-settings:LIST_VIEW_PROFILE_SORT_ASC')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </Box>
                <Box>
                    <FormControlLabel
                        control={<Switch checked={showIconArea} onChange={(_, checked) => setShowIconArea(checked)} />}
                        label={translate('lab-settings:LIST_VIEW_PROFILE_SHOW_ICON_AREA')}
                    />
                    <Typography variant="caption" color="text.secondary" component="div">
                        {translate('lab-settings:LIST_VIEW_PROFILE_SHOW_ICON_AREA_DESC')}
                    </Typography>
                </Box>
            </Stack>
        </DHDialog>
    );
};

export default ListViewProfileDialog;
